import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useConfig } from '../hooks/useConfig';
import { Settings as SettingsIcon, Save, X, ChevronDown, ChevronRight, Folder, Palette, Type, Layout, MousePointer2, Minus, Plus, Download, Upload, RotateCcw, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { ConfigField, ConfigNode } from '../types';
import { ICON_MAP } from '../constants';
import { SignedImage } from '../components/SignedImage';
import { useAuth } from '../hooks/useAuth';

const STANDARD_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Poppins', 'Playfair Display', 'JetBrains Mono'
];

const FIELD_LABELS: Record<string, string> = {
  sfondo: 'Colore Sfondo',
  voci: 'Colore Voci',
  mouseover: 'Hover (Mouse Over)',
  onclick: 'Click (Pressione)',
  selected: 'Selezionato (Attivo)',
  unselected: 'Non Selezionato (Inattivo)',
  margini: 'Margini Esterni',
  padding: 'Padding Interno',
  tipo_carattere: 'Tipo Carattere (Font)',
  dimensione: 'Dimensione Font (px)',
  formato: 'Formato Testo',
  margini_font: 'Margini Font',
  padding_font: 'Padding Font',
  path: 'Percorso',
  download: 'Percorso Download',
  upload: 'Percorso Upload',
  sfondo_generale: 'Colore Sfondo Menù',
  bordo_tipo: 'Stile Bordo',
  bordo_spessore: 'Spessore Bordo',
  bordo_colore: 'Colore Bordo',
  cartella_download: 'Cartella Download',
  cartella_upload: 'Cartella Upload',
  colore_icona: 'Colore Icona',
  sfondo_icona: 'Sfondo Icona',
  bordo_icona_colore: 'Colore Bordo Icona',
  bordo_icona_spessore: 'Spessore Bordo Icona',
  desktop_larghezza: 'Largh. Desktop',
  desktop_altezza: 'Alt. Desktop',
  desktop_sfondo: 'Sfondo Desktop',
  desktop_font_titolo: 'Font Titolo Desktop',
  desktop_colore_titolo: 'Colore Titolo Desktop',
  desktop_stile_titolo: 'Stile Titolo Desktop',
  tablet_larghezza: 'Largh. Tablet',
  tablet_altezza: 'Alt. Tablet',
  tablet_sfondo: 'Sfondo Tablet',
  tablet_font_titolo: 'Font Titolo Tablet',
  tablet_colore_titolo: 'Colore Titolo Tablet',
  tablet_stile_titolo: 'Stile Titolo Tablet',
  mobile_larghezza: 'Largh. Mobile',
  mobile_altezza: 'Alt. Mobile',
  mobile_sfondo: 'Sfondo Mobile',
  mobile_font_titolo: 'Font Titolo Mobile',
  mobile_colore_titolo: 'Colore Titolo Mobile',
  mobile_stile_titolo: 'Stile Titolo Mobile',
  sfondo_sezione: 'Sfondo Sezione',
  colore_titolo: 'Colore Titolo',
  colore_titolo_sezione: 'Colore Titolo',
  // Legacy support
  backgroundColor: 'Colore Sfondo',
  textColor: 'Colore Testo',
  textFont: 'Font Testo',
  hilight_color: 'Colore Evidenziatore',
  folder_download: 'Percorso Download',
  folder_upload: 'Percorso Upload'
};

const FORMAT_OPTIONS = [
  'normale', 'bold', 'italics', 'bold italics', 'underline', 'double-strike', 'subscript', 'superscript', 'spacing'
];

const BORDER_OPTIONS = [
  'none', 'solid', 'dashed', 'dotted', 'double', 'groove', 'ridge', 'inset', 'outset'
];

const isValidPath = (path: string) => {
  if (!path) return true;
  const winRegex = /^[a-zA-Z]:\\/;
  const unixRegex = /^\//;
  return winRegex.test(path) || unixRegex.test(path);
};

const FieldEditor: React.FC<{ 
  fieldKey: string;
  field: ConfigField;
  onChange: (newValue: string) => void;
}> = ({ fieldKey, field, onChange }) => {
  const label = FIELD_LABELS[fieldKey] || fieldKey;

  const renderInput = () => {
    switch (field.fieldType) {
      case 'Color':
        const hexColor = field.userValue && field.userValue.startsWith('#') ? field.userValue : field.defaultValue || '#000000';
        return (
          <div className="flex flex-col space-y-2 w-full">
            <div className="flex items-center space-x-2 w-full group/color">
              <div 
                className="w-10 h-10 rounded-lg border border-slate-200 shadow-sm overflow-hidden shrink-0 transition-transform group-hover/color:scale-105"
                style={{ backgroundColor: hexColor }}
              />
              <div className="flex-1 space-y-0.5">
                 <input 
                  type="color" 
                  value={hexColor} 
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full h-7 rounded-md cursor-pointer border-none bg-transparent"
                />
                <input 
                  type="text" 
                  value={field.userValue} 
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.defaultValue}
                  className="w-full bg-slate-50 border-none rounded px-1.5 py-0.5 text-[9px] font-mono focus:ring-1 focus:ring-primary/30"
                />
              </div>
            </div>
          </div>
        );
      case 'Select':
        let options: string[] = [];
        if (fieldKey.toLowerCase().includes('font') || fieldKey === 'tipo_carattere') {
          options = STANDARD_FONTS;
        } else if (fieldKey.toLowerCase().includes('formato')) {
          options = FORMAT_OPTIONS;
        } else if (fieldKey.toLowerCase().includes('bordo_tipo')) {
          options = BORDER_OPTIONS;
        } else if (fieldKey.toLowerCase().includes('stile_titolo')) {
          options = ['normal', 'bold', 'italics', 'bold italics'];
        }
        
        if (options.length > 0) {
          return (
            <div className="relative group/select">
              <select 
                className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-xs appearance-none cursor-pointer hover:border-primary/50 transition-all outline-none focus:ring-2 focus:ring-primary/10"
                value={field.userValue || field.defaultValue}
                onChange={(e) => onChange(e.target.value)}
              >
                {options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <ChevronDown className="w-3 h-3" />
              </div>
            </div>
          );
        }
        return (
          <input 
            type="text" 
            value={field.userValue} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.defaultValue}
            className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-sm"
          />
        );
      case 'NumberStepper':
        const isDimension = fieldKey === 'dimensione';
        const minVal = isDimension ? 6 : 0;
        const maxVal = isDimension ? 64 : 1000;
        const currentVal = parseInt(field.userValue) || parseInt(field.defaultValue) || 0;

        return (
          <div className="flex flex-col space-y-2 w-full">
            <div className="flex items-center space-x-2 w-full">
              <input 
                type="range"
                min={minVal}
                max={maxVal}
                value={currentVal}
                onChange={(e) => {
                  const val = field.userValue || field.defaultValue;
                  const unit = String(val).replace(/[0-9-]/g, '') || 'px';
                  onChange(`${e.target.value}${unit}`);
                }}
                className="flex-1 h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-primary hover:accent-primary/80 transition-all"
              />
              <div className="flex items-center bg-slate-50 rounded-lg border border-slate-100 px-1 py-0.5 min-w-[80px]">
                <button 
                  onClick={() => {
                    const unit = String(field.userValue || field.defaultValue).replace(/[0-9-]/g, '') || 'px';
                    onChange(`${Math.max(minVal, currentVal - 1)}${unit}`);
                  }}
                  className="p-1 hover:text-primary transition-colors"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <input 
                  type="text" 
                  value={field.userValue} 
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.defaultValue}
                  className="w-full bg-transparent border-none text-center text-[11px] font-bold focus:ring-0 p-0"
                />
                <button 
                  onClick={() => {
                    const unit = String(field.userValue || field.defaultValue).replace(/[0-9-]/g, '') || 'px';
                    onChange(`${Math.min(maxVal, currentVal + 1)}${unit}`);
                  }}
                  className="p-1 hover:text-primary transition-colors"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="flex flex-col space-y-2 w-full">
            <input 
              type="text" 
              value={field.userValue} 
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.defaultValue}
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/10 transition-all outline-none"
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-1 p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm flex flex-col text-left group hover:border-primary/30 hover:shadow-md transition-all duration-300">
      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-primary transition-colors">{label}</label>
      {renderInput()}
    </div>
  );
};

const NodeIcon: React.FC<{ node: ConfigNode, level: number }> = ({ node, level }) => {
  const [imgError, setImgError] = React.useState(false);

  if (node.iconPath && !imgError) {
    return (
      <SignedImage 
        path={node.iconPath} 
        bucket="cpgev"
        alt="" 
        className="w-5 h-5 shrink-0 object-contain"
        onError={() => setImgError(true)}
      />
    );
  }

  if (node.icon && ICON_MAP[node.icon]) {
    const IconComponent = ICON_MAP[node.icon];
    return <IconComponent className="w-5 h-5 shrink-0" />;
  }

  const name = node.item?.toLowerCase() || '';

  if (name.includes('download')) return <Download className="w-5 h-5 text-blue-500" />;
  if (name.includes('upload')) return <Upload className="w-5 h-5 text-orange-500" />;

  if (level === 0 && name) {
    if (name.includes('aspetto') || name.includes('visiv')) return <Palette className="w-5 h-5 text-purple-500" />;
    if (name.includes('file') || name.includes('cartell')) return <Folder className="w-5 h-5 text-blue-500" />;
    if (name.includes('testo') || name.includes('font')) return <Type className="w-5 h-5 text-green-500" />;
    if (name.includes('layout') || name.includes('spazi')) return <Layout className="w-5 h-5 text-orange-500" />;
    if (name.includes('interazione') || name.includes('evidenz')) return <MousePointer2 className="w-5 h-5 text-yellow-500" />;
    if (name.includes('io')) return <UserCircle className="w-5 h-5 text-primary" />;
  }

  return <SettingsIcon className="w-5 h-5 text-muted-foreground/60 shrink-0" />;
};

const RecursiveNode: React.FC<{ 
  node: ConfigNode; 
  path: string;
  onUpdate: (updater: (node: ConfigNode) => ConfigNode) => void;
  level?: number;
}> = ({ node, onUpdate, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(level === 0);
  const { user } = useAuth();
  
  const isHighlighted = useMemo(() => {
    if (!user || user.role === 'Admin' || !node.item) return false;
    return user.groups?.some(g => g.name.toLowerCase() === node.item?.toLowerCase());
  }, [user, node.item]);

  const fields = Object.entries(node).filter(([key, value]) => {
    if (key === 'item' || key === 'children' || key === 'icon' || key === 'iconPath' || key === 'route') return false;
    const isConfigField = Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && ('fieldType' in value[0] || 'userValue' in value[0]);
    if (!isConfigField) return false;
    
    // Rimuovi margini e padding dalla sezione Font come richiesto
    if (node.item === 'Font') {
      if (key.includes('margini') || key.includes('padding') || key === 'margini_font' || key === 'padding_font') return false;
    }
    
    return true;
  });

  const hasContent = (node.children && node.children.length > 0) || fields.length > 0 || (node.item && ['Sezioni', 'Icone', 'Colori', 'Dimensioni', 'Font', 'Voci Menù', 'Aspetto', 'Menù'].includes(node.item));

  if (!node.item && level > 0) {
    return (
      <div className="space-y-1.5">
        {fields.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fields.map(([key, value]) => (
              <FieldEditor 
                key={key}
                fieldKey={key}
                field={(value as ConfigField[])[0]}
                onChange={(newVal) => {
                  onUpdate(prev => {
                    const next = { ...prev };
                    const fieldArr = [...(next as any)[key]];
                    fieldArr[0] = { ...fieldArr[0], userValue: newVal };
                    (next as any)[key] = fieldArr;
                    return next;
                  });
                }}
              />
            ))}
          </div>
        )}
        {node.children && node.children.map((child, idx) => (
          <RecursiveNode 
            key={`${child.item || 'idx'}-${idx}`} 
            node={child} 
            level={level + 1}
            path={`${node.item || ''}.${idx}`}
            onUpdate={(updater) => {
              onUpdate(prev => {
                const next = { ...prev };
                if (next.children) {
                  next.children = [...next.children];
                  next.children[idx] = updater(next.children[idx]);
                }
                return next;
              });
            }}
          />
        ))}
      </div>
    );
  }

  if (!hasContent && node.item) {
    return <div className="text-sm text-muted-foreground italic py-2 ml-4">Nessun parametro in "{node.item}"</div>;
  }

  return (
    <div className={cn(
      "space-y-0.5 transition-all duration-300", 
      level > 0 && "ml-2.5 border-l border-slate-100 pl-2.5 my-1",
      isHighlighted && "p-1.5 rounded-xl border-4 border-primary/40 bg-primary/5 shadow-lg shadow-primary/10"
    )}>
      {node.item && (
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1.5 w-full py-1 px-1.5 hover:bg-slate-50 rounded-lg transition-all group"
        >
          <div className="w-4 h-4 flex items-center justify-center bg-white rounded-md shadow-sm border border-slate-100 group-hover:border-primary/30 transition-all text-slate-400">
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </div>
          <NodeIcon node={node} level={level} />
          <span className={cn(
            "font-extrabold transition-colors", 
            level === 0 ? "text-base text-slate-800" : "text-[11px] text-slate-600",
            isOpen && level === 0 && "text-primary"
          )}>{node.item}</span>
          {isHighlighted && (
            <span className="text-[10px] bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ml-2">
              Il tuo gruppo
            </span>
          )}
        </button>
      )}

      <AnimatePresence>
        {(isOpen || !node.item) && (
          <motion.div 
            initial={node.item ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden space-y-1.5 pt-0.5"
          >
            {fields.length > 0 && (
              <div className="space-y-4">
                {node.item === 'Sezioni' ? (
                  ['desktop', 'mobile', 'tablet'].map(device => {
                    const deviceFields = fields.filter(([key]) => key.startsWith(`${device}_`));
                    if (deviceFields.length === 0) return null;
                    return (
                      <div key={device} className="bg-slate-50/50 p-3 rounded-2xl border border-slate-100/50">
                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center">
                          <Layout className="w-3 h-3 mr-1.5" />
                          {device}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
                          {deviceFields.map(([key, value]) => (
                            <FieldEditor 
                              key={`${key}-${node.item}`}
                              fieldKey={key}
                              field={(value as ConfigField[])[0]}
                              onChange={(newVal) => {
                                onUpdate(prev => {
                                  const next = { ...prev };
                                  const fieldArr = [...(next as any)[key]];
                                  fieldArr[0] = { ...fieldArr[0], userValue: newVal };
                                  (next as any)[key] = fieldArr;
                                  return next;
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {fields.map(([key, value]) => (
                      <FieldEditor 
                        key={`${key}-${node.item}`}
                        fieldKey={key}
                        field={(value as ConfigField[])[0]}
                        onChange={(newVal) => {
                          onUpdate(prev => {
                            const next = { ...prev };
                            const fieldArr = [...(next as any)[key]];
                            fieldArr[0] = { ...fieldArr[0], userValue: newVal };
                            (next as any)[key] = fieldArr;
                            return next;
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Altri campi non legati ai dispositivi (es. bordi generici) */}
                {node.item === 'Sezioni' && fields.filter(([key]) => !key.includes('_')).length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                    {fields.filter(([key]) => !key.includes('_')).map(([key, value]) => (
                      <FieldEditor 
                        key={`${key}-${node.item}`}
                        fieldKey={key}
                        field={(value as ConfigField[])[0]}
                        onChange={(newVal) => {
                          onUpdate(prev => {
                            const next = { ...prev };
                            const fieldArr = [...(next as any)[key]];
                            fieldArr[0] = { ...fieldArr[0], userValue: newVal };
                            (next as any)[key] = fieldArr;
                            return next;
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {node.children && node.children.map((child, idx) => (
              <RecursiveNode 
                key={`${child.item || 'idx'}-${idx}`} 
                node={child} 
                level={level + 1}
                path={`${node.item || ''}.${idx}`}
                onUpdate={(updater) => {
                  onUpdate(prev => {
                    const next = { ...prev };
                    if (next.children) {
                      next.children = [...next.children];
                      next.children[idx] = updater(next.children[idx]);
                    }
                    return next;
                  });
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Preferences: React.FC = () => {
  const { config, updateConfig, setPreviewConfig, refreshConfig, loading } = useConfig();
  const [localConfig, setLocalConfig] = useState<any>(null);
  const initialConfigRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  // Load initial config
  React.useEffect(() => {
    if (config && config.items && initialConfigRef.current === null) {
      const configStr = JSON.stringify(config);
      initialConfigRef.current = configStr;
      setLocalConfig(JSON.parse(configStr));
    }
  }, [config]);

  const isSavedRef = useRef(false);

  // Sync preview config when local changes, but via useEffect to avoid setState during render
  React.useEffect(() => {
    if (localConfig) {
      setPreviewConfig(localConfig);
    }
  }, [localConfig]);

  // Revert preview on unmount if not saved
  React.useEffect(() => {
    return () => {
      if (!isSavedRef.current && refreshConfig) {
        if (initialConfigRef.current) {
          setPreviewConfig(JSON.parse(initialConfigRef.current));
        }
        refreshConfig();
      }
    };
  }, [refreshConfig, setPreviewConfig]);

  const isDirty = useMemo(() => {
    if (!initialConfigRef.current || !localConfig) return false;
    return initialConfigRef.current !== JSON.stringify(localConfig);
  }, [localConfig]);

  const validateAllPaths = (cfg: any): boolean => {
    let allValid = true;
    const checkNodes = (nodes: ConfigNode[]) => {
      for (const node of nodes) {
        Object.entries(node).forEach(([key, val]) => {
          if (Array.isArray(val) && val.length > 0 && val[0].fieldType === 'Text') {
            const label = FIELD_LABELS[key] || key;
            const isPath = key.toLowerCase().includes('path') || key.toLowerCase().includes('folder') || label.toLowerCase().includes('cartella');
            if (isPath && !isValidPath(val[0].userValue)) allValid = false;
          }
        });
        if (node.children) checkNodes(node.children);
      }
    };
    if (cfg.items) checkNodes(cfg.items);
    return allValid;
  };

  const handleReset = () => {
    if (initialConfigRef.current) {
      setLocalConfig(JSON.parse(initialConfigRef.current));
      toast.info('Modifiche annullate.');
    }
  };

  const handleSave = async () => {
    if (!localConfig) return;
    if (!validateAllPaths(localConfig)) {
      toast.error('Controlla i percorsi inseriti! Devono essere validi per il sistema (es. C:\\... o /...)');
      return;
    }
    setIsSaving(true);
    try {
      const success = await updateConfig(localConfig);
      if (success) {
        isSavedRef.current = true;
        toast.success('Preferenze salvate con successo!');
        initialConfigRef.current = JSON.stringify(localConfig);
        await refreshConfig();
        navigate(-1);
      } else {
        throw new Error('Salvataggio fallito (Upsert errore)');
      }
    } catch (err: any) {
      toast.error(err.message || 'Errore durante il salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading || !localConfig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground animate-pulse">Caricamento configurazione...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Preferenze Utente</h1>
          <p className="text-muted-foreground">Personalizza l'aspetto e il comportamento del sistema.</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={async () => {
              if (isDirty) {
                toast.info('Le modifiche rimarranno attive per questa sessione, ma non sono state salvate permanentemente.');
              }
              navigate(-1);
            }}
            className="flex items-center px-4 py-2 border rounded-xl font-medium hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4 mr-2" />
            Annulla
          </button>
          
          {isDirty && (
            <>
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="flex items-center px-4 py-2 border border-orange-200 text-orange-600 rounded-xl font-medium hover:bg-orange-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={cn(
                  "flex items-center px-6 py-2 rounded-xl font-semibold transition-all shadow-lg shadow-primary/20",
                  !isSaving ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Salvataggio...' : 'Consolida'}
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-orange-50 border border-orange-200 p-4 rounded-2xl flex items-start gap-3 shadow-sm"
          >
            <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
              <RotateCcw className="w-3 h-3 text-orange-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-orange-800">Modifiche non salvate</h4>
              <p className="text-xs text-orange-700/80 leading-relaxed">
                Le modifiche sono visibili immediatamente nel Menù. Puoi navigare nel sistema per testarle, 
                ma dovrai tornare qui e cliccare su <strong>"Consolida"</strong> per salvarle permanentemente nel database.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-card border rounded-2xl p-3 sm:p-4 space-y-2 shadow-sm transition-all duration-300">
        {localConfig.items.map((item: ConfigNode, idx: number) => (
          <div key={item.item ? `${item.item}-${idx}` : idx} className="space-y-2">
            <RecursiveNode 
              node={item} 
              path={`${idx}`}
              onUpdate={(updater) => {
                setLocalConfig((prev: any) => {
                  if (!prev) return prev;
                  const newConfig = JSON.parse(JSON.stringify(prev));
                  const next = updater(newConfig.items[idx]);
                  newConfig.items[idx] = next;
                  return newConfig;
                });
              }}
            />
            {idx < localConfig.items.length - 1 && <div className="h-px bg-border/50" />}
          </div>
        ))}
      </div>
    </div>
  );
};
